import { getToken } from "../storage/authStorage";
import { API_BASE_URL, CORE_API_URL } from "../../../config/api";

class APIError extends Error {
  public status: number;
  public data: any;

  constructor(message: string, status: number, data: any = null) {
    super(message);
    this.name = "APIError";
    this.status = status;
    this.data = data;
  }
}

const HTTP_STATUS = {
  UNAUTHORIZED: 401,
  INTERNAL_SERVER_ERROR: 500,
};

export interface Tax {
  tax_id: string;
  tax_name: string;
  tax_percentage: string;
  tax_type: string;
  tax_specific_type: string;
  tax_authority_id: string | null;
  tax_authority_name: string | null;
  output_tax_account_name: string | null;
  tax_account_id: string | null;
  is_inactive: string;
  is_default_tax: string;
  is_editable: string;
  tax_specification: string;
  diff_rate_reason: string;
  start_date: string | null;
  end_date: string | null;
  last_modified_time: string;
  status: string;
  created_at: string;
  updated_at: string;
}

export interface TaxSpecificationResponse {
  success: boolean;
  data: {
    taxes: Tax[];
    specification: string;
    pagination: {
      limit: number;
      offset: number;
    };
  };
}

export interface GSTDeterminationResponse {
  success: boolean;
  tax_type: string;
  gst_type: string;
  supply_type: string;
  details: {
    source_state: string;
    destination_state: string;
    source_state_normalized: string;
    destination_state_normalized: string;
    is_inter_state: boolean;
    validation_note: string;
  };
  timestamp: string;
  request_id: string;
}

export interface TDSRate {
  id: number;
  tds_tax_id: string;
  tds_tax_name: string;
  tds_tax_percentage: number;
  tds_tax_type: string;
  business_display: boolean;
  individual_display: boolean;
  is_expired: boolean;
}

export interface TDSRatesResponse {
  success: boolean;
  message: string;
  data: {
    tds_rates: TDSRate[];
    total_count: number;
  };
}

export const fetchGSTTypeDetermination = async (
  billData: any
): Promise<GSTDeterminationResponse> => {
  const token = getToken();

  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED
    );
  }

  try {
    const response = await fetch(
      `${CORE_API_URL}/api_gst_type_determination.php`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          bill_data: billData,
        }),
      }
    );

    if (!response.ok) {
      throw new APIError(
        "Failed to determine GST type",
        response.status,
        response.statusText
      );
    }

    return response.json();
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR
    );
  }
};

export const fetchTaxSpecification = async (
  specification: string = "intra"
): Promise<TaxSpecificationResponse> => {
  const token = getToken();

  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED
    );
  }

  try {
    const response = await fetch(
      `${API_BASE_URL}/api/tax/specification/${specification}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (!response.ok) {
      throw new APIError(
        "Failed to fetch tax specification",
        response.status,
        response.statusText
      );
    }

    return response.json();
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR
    );
  }
};

export const fetchTDSRates = async (): Promise<TDSRatesResponse> => {
  const url = `${API_BASE_URL}/api/get-tds`;
  const response = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  const data: TDSRatesResponse = await response.json();
  return data;
};
